import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import Label from '../../components/foundations/Label';
import withTranslate from '../../containers/widgets/withTranslate';
import { Button } from '../../components/widgets';
import { ALERT_LEVELS, CAP_DISPLAY_NAMES, CAP_TYPES } from '../consts';
import css from './CapsList.css';

const USAGE_TAG_CLASS_MAP = {
    [ALERT_LEVELS.BLOCKED]: css.tagError,
    [ALERT_LEVELS.HARSH_WARNING]: css.tagError,
    [ALERT_LEVELS.MODERATE_WARNING]: css.tagWarning,
    [ALERT_LEVELS.GENTLE_WARNING]: css.tagWarning,
};

const USAGE_TAG_TEXT_MAP = {
    [ALERT_LEVELS.BLOCKED]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.BLOCKED_USAGE',
    [ALERT_LEVELS.HARSH_WARNING]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.EXTRA_HIGH_USAGE',
    [ALERT_LEVELS.MODERATE_WARNING]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MODERATE_USAGE',
    [ALERT_LEVELS.GENTLE_WARNING]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.HIGH_USAGE',
};

const getUsageTagClassName = alertLevel => {
    return USAGE_TAG_CLASS_MAP[alertLevel] || css.tagHealthy;
};

const getUsageTagText = alertLevel => {
    return USAGE_TAG_TEXT_MAP[alertLevel] || 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.HEALTHY_USAGE';
};

const CapsList = ({
    alerts = [],
    caps = {},
    translate = () => '',
    defaultExpanded = false,
    isPaused = false,
    onAuditEvents,
    isAuditing = false,
    actualEvents = 0,
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const alertMap = alerts.reduce((acc, alert) => {
        acc[alert.metric] = alert;
        return acc;
    }, {});

    const renderCapItem = (capKey, capValue) => {
        // Check if there's an alert for this cap type (metric matches cap key)
        const alert = alertMap[capKey];
        const alertLevel = alert?.alertLevel || ALERT_LEVELS.NONE;
        const capName = translate(CAP_DISPLAY_NAMES[capKey] || capKey);
        const isBlocked = alertLevel === ALERT_LEVELS.BLOCKED;

        return (
            <div key={capKey} className={css.capItem}>
                <span className={classNames(css.capName, { [css.blockedText]: isBlocked })}>
                    {capName} - {capValue.toLocaleString()}
                </span>
                {!isPaused && (
                    <span className={classNames(css.usageTag, getUsageTagClassName(alertLevel))}>
                        <Icon
                            name="activity-heartbeat"
                            size={IconSizes.X_SMALL}
                            className={css.tagIcon}
                            interactive={false}
                        />
                        <Translate id={getUsageTagText(alertLevel)} />
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className={css.capsListContainer}>
            <button
                type="button"
                className={css.toggleButton}
                onClick={() => setIsExpanded(prevExpanded => !prevExpanded)}
            >
                <Icon
                    name={isExpanded ? 'chevron-up' : 'chevron-down'}
                    size={IconSizes.X_SMALL}
                    className={css.toggleIcon}
                />
                <Translate
                    id={isExpanded ? 'STATIC.PAGES.ACCOUNT_PLAN.SHOW_LESS' : 'STATIC.PAGES.ACCOUNT_PLAN.SHOW_MORE'}
                />
            </button>
            {isExpanded && (
                <div className={css.capsContent}>
                    <Label text="STATIC.PAGES.ACCOUNT_PLAN.PLAN_INCLUDES_CAPS" className={css.capsTitle} />
                    <div className={css.capsList}>
                        {Object.entries(caps).map(([capKey, capValue]) => renderCapItem(capKey, capValue))}
                    </div>
                    {caps[CAP_TYPES.EVENT] ? (
                        <div className={css.capItem}>
                            <span className={css.capName}>
                                {translate('STATIC.PAGES.ACCOUNT_PLAN.DAILY_RUN_RATE_TARGET')}
                                {' - '}
                                {Math.round(caps[CAP_TYPES.EVENT] / 365).toLocaleString()}
                            </span>
                        </div>
                    ) : null}
                    {caps[CAP_TYPES.EVENT] ? (
                        <div className={css.capItem}>
                            <span className={css.capName}>
                                {translate('STATIC.PAGES.ACCOUNT_PLAN.ACTUAL_EVENT_PACING')}
                                {' - '}
                                {actualEvents.toLocaleString()}
                            </span>
                        </div>
                    ) : null}
                    {caps[CAP_TYPES.EVENT] && onAuditEvents ? (
                        <Button
                            type="secondary"
                            className={css.auditButton}
                            text="STATIC.PAGES.ACCOUNT_PLAN.BUTTONS.AUDIT_EVENTS"
                            onClick={onAuditEvents}
                            disabled={isAuditing}
                            showSpinner={isAuditing}
                        />
                    ) : null}
                </div>
            )}
        </div>
    );
};

CapsList.propTypes = {
    alerts: PropTypes.arrayOf(
        PropTypes.shape({
            metric: PropTypes.string.isRequired,
            alertLevel: PropTypes.number.isRequired,
        })
    ),
    caps: PropTypes.objectOf(PropTypes.number),
    translate: PropTypes.func,
    defaultExpanded: PropTypes.bool,
    isPaused: PropTypes.bool,
    onAuditEvents: PropTypes.func,
    isAuditing: PropTypes.bool,
    actualEvents: PropTypes.number,
};

export default withTranslate(CapsList);
